import { useEffect, useState, useCallback } from "react";
import api, { money } from "../lib/api";
import { exportExcel, exportPDF, exportCSV } from "../lib/export";
import {
  FileSpreadsheet, FileDown, FileText, BarChart3, Filter, X,
  TrendingUp, TrendingDown, Wallet, AlertTriangle, Banknote, Building2,
} from "lucide-react";
import { Button } from "../components/ui/button";

const REPORTS = [
  { key: "income-statement", label: "Income Statement (P&L)" },
  { key: "property-performance", label: "Property Performance" },
  { key: "cash-flow", label: "Cash Flow" },
  { key: "balance-sheet", label: "Balance Sheet" },
  { key: "rent-roll", label: "Rent Roll" },
  { key: "expenses-by-category", label: "Expenses by Category" },
  { key: "outstanding-rent", label: "Outstanding Rent" },
  { key: "security-deposit", label: "Security Deposit" },
  { key: "tenant-history", label: "Tenant Payment History" },
  { key: "monthly-summary", label: "Monthly Summary" },
  { key: "ytd", label: "Year-to-Date" },
  { key: "checklists", label: "Move-In / Move-Out Checklists" },
  { key: "compliance", label: "Compliance" },
  { key: "communications", label: "Tenant Communication" },
];

const KPIS = [
  ["rent_collected", "Rent Collected", TrendingUp, "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10"],
  ["total_expenses", "Total Expenses", TrendingDown, "bg-red-50 text-red-600 dark:bg-red-500/10"],
  ["net_operating_income", "Net Operating Income", Wallet, "bg-blue-50 text-blue-600 dark:bg-blue-500/10"],
  ["mortgage_payments", "Mortgage Payments", Banknote, "bg-violet-50 text-violet-600 dark:bg-violet-500/10"],
  ["cash_flow", "Cash Flow", BarChart3, "bg-amber-50 text-amber-600 dark:bg-amber-500/10"],
  ["outstanding_rent", "Outstanding Rent", AlertTriangle, "bg-orange-50 text-orange-600 dark:bg-orange-500/10"],
];

const fmtCell = (v, col) => {
  if (col.money) return money(v);
  if (col.key === "percent" || col.key === "cap_rate" || col.key === "roi" || col.key === "occupancy") return `${v}%`;
  return v ?? "—";
};

export default function Reports() {
  const [filters, setFilters] = useState({});
  const [opts, setOpts] = useState({ properties: [], tenants: [], bank_accounts: [], income_categories: [], expense_categories: [], payment_statuses: [] });
  const [dash, setDash] = useState(null);
  const [reportKey, setReportKey] = useState("income-statement");
  const [report, setReport] = useState(null);

  useEffect(() => { api.get("/reports/filter-options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  useEffect(() => { api.get(`/reports/dashboard${qs()}`).then((r) => setDash(r.data)).catch(() => {}); }, [qs]);
  useEffect(() => { setReport(null); api.get(`/reports/${reportKey}${qs()}`).then((r) => setReport(r.data)).catch(() => {}); }, [reportKey, qs]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v || undefined }));
  const clearFilters = () => setFilters({});
  const activeCount = Object.values(filters).filter(Boolean).length;

  const doExport = (kind) => {
    if (!report) return;
    const cols = report.columns;
    if (kind === "excel") exportExcel(report.title, cols, report.rows);
    else if (kind === "csv") exportCSV(report.title.replace(/\s+/g, "-").toLowerCase(), cols, report.rows);
    else exportPDF(report.title, cols, report.rows);
  };

  return (
    <div className="space-y-6" data-testid="page-reports">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Financials</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><BarChart3 className="w-7 h-7 text-primary" /> Reports & Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Portfolio financial performance, statements, and exportable reports.</p>
        </div>
      </div>

      {/* Filter bar */}
      <section className="rounded-2xl border border-border bg-card p-4" data-testid="reports-filter-bar">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium">
          <Filter className="w-4 h-4 text-primary" /> Filters
          {activeCount > 0 && <button onClick={clearFilters} data-testid="reports-clear-filters" className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /> Clear ({activeCount})</button>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Select label="Property" testid="filter-property" value={filters.property_id} onChange={(v) => setF("property_id", v)} options={[["", "All properties"], ...opts.properties.map((p) => [p.id, p.name])]} />
          <Select label="Tenant" testid="filter-tenant" value={filters.tenant_id} onChange={(v) => setF("tenant_id", v)} options={[["", "All tenants"], ...opts.tenants.map((t) => [t.id, t.name])]} />
          <Field label="From" testid="filter-date-from"><input type="date" value={filters.date_from || ""} onChange={(e) => setF("date_from", e.target.value)} className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Field>
          <Field label="To" testid="filter-date-to"><input type="date" value={filters.date_to || ""} onChange={(e) => setF("date_to", e.target.value)} className="w-full rounded-lg border border-border bg-background p-2 text-sm" /></Field>
          <Select label="Income Category" testid="filter-income-cat" value={filters.income_category} onChange={(v) => setF("income_category", v)} options={[["", "All income"], ...opts.income_categories.map((c) => [c, c])]} />
          <Select label="Expense Category" testid="filter-expense-cat" value={filters.expense_category} onChange={(v) => setF("expense_category", v)} options={[["", "All expenses"], ...opts.expense_categories.map((c) => [c, c])]} />
          <Select label="Payment Status" testid="filter-status" value={filters.status} onChange={(v) => setF("status", v)} options={[["", "All statuses"], ...opts.payment_statuses.map((s) => [s, s])]} />
          <Select label="Bank Account" testid="filter-account" value={filters.bank_account} onChange={(v) => setF("bank_account", v)} options={[["", "All accounts"], ...opts.bank_accounts.map((a) => [a, a])]} />
        </div>
      </section>

      {/* Financial dashboard */}
      {dash && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {KPIS.map(([key, label, Icon, accent], i) => (
              <div key={key} className="rounded-2xl border border-border bg-card p-4 kpi-enter" style={{ animationDelay: `${i * 40}ms` }} data-testid={`report-kpi-${key}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${accent}`}><Icon className="w-4.5 h-4.5" strokeWidth={1.75} /></div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
                <p className={`font-heading text-lg font-bold tracking-tight mt-0.5 ${dash[key] < 0 ? "text-red-600" : ""}`}>{money(dash[key])}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="report-profitability">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Building2 className="w-4.5 h-4.5 text-primary" /> Profitability by Property</h3>
              <div className="space-y-2">
                {dash.profitability_by_property.length === 0 && <p className="text-sm text-muted-foreground">No data for this filter.</p>}
                {dash.profitability_by_property.map((p) => (
                  <div key={p.name} className="flex items-center justify-between text-sm">
                    <span className="truncate">{p.name}</span>
                    <span className={`font-mono font-semibold ${p.net < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(p.net)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5" data-testid="report-expense-breakdown">
              <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><TrendingDown className="w-4.5 h-4.5 text-primary" /> Expenses by Category</h3>
              <div className="space-y-2">
                {dash.expenses_by_category.length === 0 && <p className="text-sm text-muted-foreground">No expenses for this filter.</p>}
                {dash.expenses_by_category.slice(0, 8).map((c) => {
                  const max = dash.expenses_by_category[0]?.value || 1;
                  return (
                    <div key={c.name}>
                      <div className="flex justify-between text-sm mb-0.5"><span>{c.name}</span><span className="font-mono">{money(c.value)}</span></div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary/70" style={{ width: `${(c.value / max) * 100}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Report selector + export */}
      <section className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <FileText className="w-4.5 h-4.5 text-primary" />
            <select value={reportKey} onChange={(e) => setReportKey(e.target.value)} data-testid="report-selector"
              className="rounded-lg border border-border bg-background p-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30">
              {REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => doExport("csv")} data-testid="export-csv"><FileDown className="w-4 h-4 mr-1.5" /> CSV</Button>
            <Button variant="outline" className="rounded-xl" onClick={() => doExport("excel")} data-testid="export-excel"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel</Button>
            <Button className="rounded-xl" onClick={() => doExport("pdf")} data-testid="export-pdf"><FileDown className="w-4 h-4 mr-1.5" /> PDF</Button>
          </div>
        </div>

        {report ? (
          <>
            {report.summary?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b border-border bg-muted/30">
                {report.summary.map((s) => (
                  <div key={s.label} data-testid={`report-summary-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</p>
                    <p className={`font-heading text-xl font-bold mt-0.5 ${s.negative ? "text-red-600" : ""}`}>{s.money ? money(s.value) : s.value}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="report-table">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>{report.columns.map((c) => <th key={c.key} data-testid={`report-col-${c.key}`} className={`px-4 py-3 font-semibold ${c.money ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {report.rows.length === 0 && <tr><td colSpan={report.columns.length} className="px-4 py-10 text-center text-muted-foreground">No data for the selected filters.</td></tr>}
                  {report.rows.map((r, i) => {
                    const rowKey = `${r[report.columns[0]?.key] ?? ""}-${i}`;
                    return (
                    <tr key={rowKey} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`report-row-${i}`}>
                      {report.columns.map((c) => (
                        <td key={c.key} data-testid={`report-cell-${i}-${c.key}`} className={`px-4 py-3 ${c.money ? "text-right font-mono" : ""} ${c.money && r[c.key] < 0 ? "text-red-600" : ""}`}>{fmtCell(r[c.key], c)}</td>
                      ))}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : <div className="p-10 text-center text-muted-foreground text-sm">Loading report…</div>}
      </section>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <Field label={label} testid={testid}>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </Field>
  );
}
